// 服务条款 / Terms of Service
//
// Audience: Chinese-speaking users primarily (per PRD §1 主战场 = 中文 STEM
// 学术 + 教培市场). English equivalents in parentheses for legal clarity
// where Chinese terminology is ambiguous. Stripe Checkout displays the
// /terms URL we configure in the Dashboard; this page must be reachable
// without auth.

import { Link } from 'react-router-dom';
import { LegalFooter } from '../components/LegalFooter';
import { TopBar } from '../components/TopBar';
import { PublicNavLinks } from '../components/PublicNavLinks';
import './Legal.css';

export function Terms() {
  return (
    <div className="legal-page">
      <TopBar links={<PublicNavLinks />} />

      <main className="legal-shell">
        <h1>服务条款 <span className="legal-en">/ Terms of Service</span></h1>
        <p className="legal-meta">最后更新：2026-05-06 · v1.0</p>

        <section>
          <p>
            欢迎使用 <strong>AutoLecture</strong>（"我们"、"本服务"）。本服务条款
            （"条款"）构成您（"用户"、"您"）与 AutoLecture 之间具有法律约束力的协议。
            访问或使用本服务即表示您同意受这些条款约束。如不同意，请勿使用本服务。
          </p>
        </section>

        <section>
          <h2>1. 服务描述</h2>
          <p>
            AutoLecture 是基于 LaTeX 语法的 AI 视频生产工具，用户编写 VideoTeX
            源码、点击编译，得到讲座视频（mp4）。服务包含：代码编辑器（CodeMirror 6）、
            AI 视觉生成（Manim / HTML / Wan / Seedance 等）、文字转语音（TTS）、
            语音转写（Whisper ASR）、文件存储、视频导出。
          </p>
          <p>
            服务用量以"成片分钟"（每月可编译产出的视频时长）和
            "AI 视频秒"（Wan / Seedance 生成的视频秒数）计量，
            各订阅档位的月度额度、单视频长度上限与存储空间见定价页。
            额度按自然月重置；超出时编译会被暂停，可等待重置、升级套餐
            或购买 AI 视频秒加购包。预估用量在编译前向用户展示。
          </p>
        </section>

        <section>
          <h2>2. 账户与年龄</h2>
          <p>
            注册账户需提供有效的电子邮箱并完成验证（6 位验证码）。您必须年满
            <strong>16 周岁</strong>（或您所在司法辖区允许独立缔约的最低年龄）。
            未满该年龄者必须由父母或法定监护人代为同意并使用。
          </p>
          <p>
            您对账户下所有活动负责，包括保护账户凭证、不与他人共享账户、
            发现未授权使用立即通知我们（{' '}
            <a href="mailto:codescao7@gmail.com">codescao7@gmail.com</a>{' '}）。
          </p>
        </section>

        <section>
          <h2>3. 使用规则</h2>
          <p>使用本服务时，您同意 <strong>不</strong>：</p>
          <ul>
            <li>生成或上传<strong>违反所在司法辖区法律</strong>的内容（包括但不限于：暴力、恐怖主义、未成年人不适宜、毒品、色情、欺诈）</li>
            <li>侵犯他人<strong>知识产权</strong>（版权、商标、专利）或<strong>肖像权 / 名誉权</strong>，包括：未经授权使用他人录音生成 voice clone、未授权复制他人课程内容</li>
            <li><strong>冒充</strong>他人或捏造身份（例如生成"某老师"的合成音频用于误导）</li>
            <li>使用本服务进行<strong>恶意活动</strong>：绕过付费墙、滥发垃圾信息、自动化抓取、攻击或干扰服务可用性</li>
            <li><strong>逆向工程</strong>、爬取本服务、批量下载非自己创作的内容</li>
            <li>违反 Stripe、Cloudflare、阿里云 DashScope 等第三方服务商的可接受使用政策</li>
          </ul>
          <p>
            违规使用可导致账户立即终止、credits 不退还、且我们可能向有关部门报告。
          </p>
        </section>

        <section>
          <h2>4. 用户生成内容（您的 .tex 与导出 mp4）</h2>
          <p>
            您写入 .tex 文件的内容、上传的素材（图片、音频、视频）、以及编译产出的
            mp4 视频，<strong>所有权归您</strong>。我们仅出于以下目的存储：
          </p>
          <ul>
            <li>向您交付服务（编译需要读取您的源码）</li>
            <li>缓存渲染产物以避免重复扣费（hash-keyed cache）</li>
            <li>必要的备份与故障恢复</li>
          </ul>
          <p>
            您授予我们<strong>非排他、全球、免费</strong>的许可，仅限上述运行服务所需范围使用您的内容。
            我们 <strong>不会</strong>：把您的源码或视频用于训练第三方模型、向其他用户披露、
            或在未经授权的情况下用作我们的营销素材。
          </p>
          <p>
            <strong>AI 生成内容免责</strong>：本服务使用大语言模型（Qwen 等）生成
            Manim Python 代码、HTML 代码、视频脚本、TTS 音频。AI 输出可能包含错误、
            不准确或与他人作品相似的内容。我们 <strong>不保证</strong>{' '}
            AI 生成内容的唯一性、准确性、或不侵权性。您发布前应自行审核。
          </p>
        </section>

        <section>
          <h2>5. 计费、退款与争议</h2>
          <p>
            <strong>付款</strong>：所有付款通过 <strong>Stripe</strong> 处理。我们不存储您的
            完整支付卡信息（仅保留 Stripe customer id 用于订阅续费）。
          </p>
          <p>
            <strong>credits 消耗规则</strong>：
          </p>
          <ul>
            <li>credits 在执行付费操作（编译、转录等）的<strong>那一刻</strong>从余额中实际扣除</li>
            <li>编译失败时 credits 通常 <strong>退还</strong>（除非失败由用户输入引发，例如 LaTeX 语法错误）</li>
            <li>缓存命中（cache hit）的视图块不重新扣费</li>
          </ul>
          <p>
            <strong>退款政策</strong>：
          </p>
          <ul>
            <li><strong>已消耗的 credits 不退款。</strong>因为 AI 生成、视频渲染等操作已经发生了真实的算力 / 第三方 API 成本</li>
            <li><strong>未消耗的 credits 自购买之日起 <span className="legal-strong">14 天内</span>可申请退款</strong>（按 EU 消费者权益指令 + 中国《消法》七日无理由先例的较高标准）。请发邮件至 {' '}<a href="mailto:codescao7@gmail.com">codescao7@gmail.com</a> 说明购买流水号</li>
            <li>订阅型 credits（每月发放）仅按未发放部分按比例退款</li>
            <li>退款仅原路返回至付款渠道（Stripe），到账时间以发卡行为准（通常 5-10 个工作日）</li>
          </ul>
          <p>
            <strong>订阅与续费</strong>：订阅按月或按年自动续费。您可随时通过{' '}
            <Link to="/account">"Account → Manage subscription"</Link>{' '}（即 Stripe Customer Portal）
            <strong>自助取消</strong>。取消后当前付费周期内服务保持有效，到期不再扣费。
          </p>
          <p>
            <strong>价格变更</strong>：我们可能调整定价、tier 包含的 credit 数、
            或外部 API markup。变更对<strong>未来周期</strong>生效，不影响已支付的订阅期；
            涨价我们提前 14 天邮件通知，您可在生效前取消。
          </p>
          <p>
            <strong>异议处理</strong>：对扣费有异议请先联系{' '}
            <a href="mailto:codescao7@gmail.com">codescao7@gmail.com</a>。我们承诺在 5 个工作日内响应。
            未能解决可向 Stripe 发起 chargeback 或诉诸主管机关。
          </p>
        </section>

        <section>
          <h2>6. 服务可用性与变更</h2>
          <p>
            本服务按"现状"（"as is"）提供。我们持续改进，但不保证：
          </p>
          <ul>
            <li>服务 100% 不中断（计划维护、故障、第三方 API 故障等可能导致暂时不可用）</li>
            <li>所有 AI 生成结果都符合您的预期（输出受 prompt、模型版本、随机种子等影响）</li>
            <li>所有外部 API（Stripe、DashScope、Wan 视频）始终可用</li>
          </ul>
          <p>
            服务功能、定价、使用上限可能随时调整。重大变更我们会以邮件或站内通知告知。
          </p>
        </section>

        <section>
          <h2>7. 责任限制</h2>
          <p>
            在适用法律允许的最大范围内，AutoLecture 及其关联方对以下情形<strong>不承担责任</strong>：
          </p>
          <ul>
            <li>因 AI 生成内容不准确、与第三方作品相似等导致的间接损失</li>
            <li>因您违规使用本服务导致的第三方索赔</li>
            <li>因第三方服务（Stripe、DashScope、电信运营商）故障导致的损失</li>
            <li>因您的设备、网络问题导致的数据丢失（请定期下载导出您的 .tex 与 mp4）</li>
            <li>不可抗力（自然灾害、政府行为、网络中断）</li>
          </ul>
          <p>
            我们对您的累计赔偿责任，<strong>不超过</strong>您过去 12 个月内向本服务实际支付的金额。
          </p>
        </section>

        <section>
          <h2>8. 终止</h2>
          <p>
            您可随时通过删除账户终止本协议。我们也可在您严重违反本条款时立即终止您的账户，
            并保留追究法律责任的权利。终止后：
          </p>
          <ul>
            <li>您的 .tex 与 mp4 文件保留至少 <strong>30 天</strong>，您可通过下载工具自助导出</li>
            <li>未消耗的 credits 自终止之日起 14 天内仍可申请退款（按§5 政策）</li>
            <li>终止前已发生的争议条款（赔偿、知识产权、适用法律）仍然有效</li>
          </ul>
        </section>

        <section>
          <h2>9. 适用法律与争议解决</h2>
          <p>
            本条款受<strong>中华人民共和国</strong>法律管辖。因本协议引起的争议，
            双方应首先友好协商；协商不成的，提交服务运营方所在地有管辖权的人民法院诉讼解决。
          </p>
          <p>
            如您是 EU 居民，欧盟消费者权益法（包括 14 天冷静期）作为本条款的最低标准适用。
          </p>
        </section>

        <section>
          <h2>10. 条款变更</h2>
          <p>
            我们可能不时更新本条款。重大变更（影响您权利或费用的条款）将通过邮件或站内通知
            <strong>提前 14 天</strong>告知。继续使用本服务即视为接受变更。
          </p>
        </section>

        <section>
          <h2>11. 联系方式</h2>
          <p>
            服务运营方：AutoLecture<br />
            邮箱：<a href="mailto:codescao7@gmail.com">codescao7@gmail.com</a><br />
            网站：<a href="https://autolecture.ai">https://autolecture.ai</a><br />
            源码（开源部分）：<a href="https://github.com/scao7/autolecture" target="_blank" rel="noreferrer">github.com/scao7/autolecture</a>
          </p>
        </section>

        <p className="legal-foot muted small">
          本条款的中文版为正式版本。如需英文翻译用于跨境合规，请邮件联系。
        </p>
      </main>

      <LegalFooter />
    </div>
  );
}
